/**
 * Brand — the single source of truth for the product identity a user sees.
 *
 * Decision (Haris, 2026-09-06): everything user-facing is **King.Prostor**,
 * every mail is sent from `podpora@kingprostor.si`, every emailed link lands
 * on a kingprostor.si host. "GSX8" is the repository's codename, "GeoSX" the
 * retired 2026 brand experiment, "SL-King d.o.o." the legal entity — none of
 * them belongs in copy, subjects, titles or footers (the one exception: the
 * legal entity is the data controller on /zasebnost, `LEGAL_ENTITY`). Desktop product names
 * (KingCAD, Sewer+, King.D96) and `capp` licence codes are NOT brand strings
 * and stay as they are.
 *
 * Shared (not server) so both the Fastify placeholders and client code read
 * the same values. Pure: no env, no Node APIs.
 *
 * Domain families: the geosx.ai / geosx.io hosts still serve the app, so
 * cookie domain, OAuth callback and tile CDN remain per family — that is
 * infrastructure, not brand. `domainFamily()` is the ONE suffix test; the
 * planned international family (kingprostor.ai) is a row in DOMAIN_FAMILIES
 * plus its cookie/CDN/OAuth entries in domainHelpers.ts.
 */

export const BRAND = {
  name: 'King.Prostor',
  siteHost: 'kingprostor.si',
  siteUrl: 'https://kingprostor.si',
  /** Shown in footers AND used as the From address — replies must reach support. */
  supportEmail: 'podpora@kingprostor.si',
  /** The auth pages' second line; one place to change. */
  tagline: 'ker je naša mapa kraljevska!',
} as const;

/** RFC 5322 From value; the env-schema default for AWS_SES_FROM_EMAIL. */
export const BRAND_MAIL_FROM = `${BRAND.name} <${BRAND.supportEmail}>`;

/**
 * The legal entity — named as the data controller on the privacy page
 * (/zasebnost, #790) and nowhere else: a controller must be the registered
 * company, never the brand. Registered form as on the public site's footer
 * (kingprostor.si) and in the installer licence texts (kingcad). Never
 * »King Prostor d.o.o.« — that company does not exist.
 */
export const LEGAL_ENTITY = {
  name: 'SL-King d.o.o.',
  address: 'Dunajska cesta 113, 1000 Ljubljana',
} as const;

/**
 * Fill the `{{…}}` placeholders of a served HTML page. One function for the
 * Fastify onSend hook (production) and the Vite transformIndexHtml twin (dev),
 * so the two lists cannot drift; the page tests apply the same function.
 */
export function fillBrandPlaceholders(html: string, tilesDomain: string): string {
  return html
    .replaceAll('{{BRAND_NAME}}', BRAND.name)
    .replaceAll('{{BRAND_TAGLINE}}', BRAND.tagline)
    .replaceAll('{{TILES_DOMAIN}}', tilesDomain)
    .replaceAll('{{SUPPORT_EMAIL}}', BRAND.supportEmail)
    .replaceAll('{{COMPANY_NAME}}', LEGAL_ENTITY.name)
    .replaceAll('{{COMPANY_ADDRESS}}', LEGAL_ENTITY.address);
}

/** Tri-colour wordmark for pages (Tailwind classes; the auth pages carry this exact markup). */
export const BRAND_WORDMARK_HTML =
  '<span class="text-green-800">King</span><span class="text-red-600">.</span><span class="text-orange-500">Prostor</span>';

/** Same wordmark for mail: inline colours (green-800 / red-600 / orange-500) — mail clients strip stylesheets. */
export const BRAND_WORDMARK_EMAIL_HTML =
  '<span style="color:#166534;">King</span><span style="color:#dc2626;">.</span><span style="color:#f97316;">Prostor</span>';

export type DomainFamily = 'kingprostor' | 'geosxai' | 'geosxio' | 'local' | 'unknown';

/** One row per served domain family. Order matters only for readability. */
const DOMAIN_FAMILIES: ReadonlyArray<{ family: Exclude<DomainFamily, 'local' | 'unknown'>; apex: string }> = [
  { family: 'kingprostor', apex: 'kingprostor.si' },
  { family: 'geosxai', apex: 'geosx.ai' },
  { family: 'geosxio', apex: 'geosx.io' },
];

/**
 * Must LOOK like a DNS name before any suffix test: a forwarded value of
 * `attacker.example/.geosx.ai` also ends with a real suffix, and interpolated
 * into `https://` it navigates to attacker.example (the trap SupportTicketService
 * documented at its old getBaseUrl).
 */
const DNS_NAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?([.][a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/** Lower-cased hostname without a port ("dev.kingprostor.si:4002" → "dev.kingprostor.si"). */
export function hostOf(hostname: string): string {
  return (hostname.split(':')[0] ?? hostname).trim().toLowerCase();
}

/**
 * Which served family a request hostname belongs to. `local` for the Vite /
 * Fastify dev origins, `unknown` for anything that is not a DNS name under
 * one of our apexes (a spoofed X-Forwarded-Host lands here — callers must
 * never echo an `unknown` host into a link).
 */
export function domainFamily(hostname: string): DomainFamily {
  const host = hostOf(hostname);
  if (host === 'localhost' || host.endsWith('.localhost') || host === '127.0.0.1') return 'local';
  if (!DNS_NAME.test(host)) return 'unknown';
  for (const { family, apex } of DOMAIN_FAMILIES) {
    if (host === apex || host.endsWith(`.${apex}`)) return family;
  }
  return 'unknown';
}
